import type { ReactNode } from 'react';
import type { TreeProperties } from '../lib/types';

/**
 * What we know about one tree, as a card.
 *
 * Shared by the two ways this surfaces. With a mouse it follows the cursor on
 * hover (TreeTooltip); on a touchscreen there is no hover to follow, so a tap
 * pins it above the sheet (TreeInspector). Only the framing differs, so only
 * the framing lives in those two files.
 *
 * The 2023 popup also listed Canopy Radius, Height and Density. Those fields do
 * not exist in the tree data (only tree_dbh, status, health, address, species
 * and location do), so they rendered as "undefined" on every tree. They are
 * omitted here rather than shown empty.
 */
export function TreeCard({
  properties,
  hint,
  children,
}: {
  properties: TreeProperties;
  /** Trailing line of guidance. Omitted when the tree has nothing to load. */
  hint?: string;
  /** Actions, on touch where there is no click-through to the map beneath. */
  children?: ReactNode;
}) {
  const rows: Array<[string, string]> = [];
  const noCanopy = properties.has_canopy === 0;

  if (properties.address) rows.push(['Address', titleCase(properties.address)]);
  if (properties.nta_name) rows.push(['Neighborhood', properties.nta_name]);
  if (properties.health) rows.push(['Health', properties.health]);
  if (properties.status) rows.push(['Status', properties.status]);
  if (properties.tree_dbh != null) rows.push(['Trunk diameter', `${properties.tree_dbh} in`]);

  return (
    <>
      <div className="tip__name">{properties.spc_common ?? 'Street tree'}</div>
      {properties.spc_latin && <div className="tip__latin">{properties.spc_latin}</div>}

      {noCanopy && (
        <div className="tip__alert">
          <span className="tip__alert-icon" aria-hidden="true">
            ▲
          </span>
          No LiDAR canopy available
        </div>
      )}

      <dl className="tip__rows">
        {rows.map(([label, value]) => (
          <div className="tip__row" key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>

      {!noCanopy && hint && <div className="tip__hint">{hint}</div>}
      {children}
    </>
  );
}

function titleCase(s: string): string {
  return s
    .toLowerCase()
    .replace(/\b[a-z]/g, (c) => c.toUpperCase())
    .replace(/\b(\d+)(st|nd|rd|th)\b/gi, (_, n, suf) => `${n}${suf.toLowerCase()}`);
}
